import { useCallback, useMemo } from 'react'
import { Outlet, To } from 'react-router-dom'

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { TrashBinDocumentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import type { SPACE } from 'modules/documents/lib/constants'
import { asDocuments } from 'modules/documents/components/dataCollectionAdapters'

import type { TrashBinDocumentNode } from './types'
import { useFilters } from './useFilters'
import { useColumns } from './useColumns'
import { useBulkActions } from './useBulkActions'
import { useItemActions } from './useItemActions'

const ITEMS_PER_PAGE = 100

type Props = {
  space: SPACE
  getItemRoute: (documentId: string) => string
  parentRoute: To
}

const TrashBinDataCollection = ({ space, getItemRoute, parentRoute }: Props) => {
  const { filters, loading: filtersLoading } = useFilters(space)
  const { sortings, defaultSortings, columns } = useColumns(space)
  const { bulkActions, handleBulkAction, Modals } = useBulkActions()
  const { itemActions, ItemActionModals } = useItemActions()

  const source = useGraphqlDataCollectionSource({
    document: TrashBinDocumentsDataCollectionDocument,
    paginationType: 'pages',
    perPage: ITEMS_PER_PAGE,
    includePaginationVars: false,

    mapData: (data) => {
      const edges = data?.documents?.documentsConnection?.edges ?? []
      return edges.map((edge) => edge.node).filter(Boolean)
    },

    mapVariables: ({ filters: filterState, sortings: sortingsState, search, pagination }) => {
      const filter: Record<string, unknown> = {}

      if (filterState?.deletedAt) {
        const raw = filterState.deletedAt
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const from = 'from' in raw && raw.from instanceof Date ? raw.from : undefined
          const to = 'to' in raw && raw.to instanceof Date ? raw.to : undefined
          if (from && to) {
            filter.deletedAt = {
              between: [from.toISOString(), to.toISOString()],
            }
          }
        }
      }

      if (filterState?.employee) {
        const employees = Array.isArray(filterState.employee)
          ? filterState.employee.filter((v): v is string => typeof v === 'string')
          : []
        if (employees.length > 0) {
          filter.employee = { in: employees }
        }
      }

      if (filterState?.folder) {
        const folders = Array.isArray(filterState.folder)
          ? filterState.folder.filter((v): v is string => typeof v === 'string')
          : []
        if (folders.length > 0) {
          filter.folder = { in: folders }
        }
      }

      return {
        space,
        search: search || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        sortOrder: sortingsState?.[0],
        first: pagination?.perPage ?? ITEMS_PER_PAGE,
        offsetPage: pagination?.currentPage ?? 1,
        byTrashBin: true,
      }
    },

    mapPagination: (data) => {
      const connection = data?.documents?.documentsConnection
      return {
        total: connection?.totalCount ?? 0,
      }
    },

    search: {
      enabled: true,
    },
    filters,
    sortings,
    defaultSortings,
    selectable: (item: TrashBinDocumentNode) => item.id,
    bulkActions,
    itemActions,
    itemUrl: useCallback((item: TrashBinDocumentNode) => getItemRoute(item.id), [getItemRoute]),
  })

  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records
  const documentsFromCollection = useMemo(() => asDocuments(records), [records])

  const contextValue = useMemo(
    () => ({
      documentsEntity: documentsFromCollection,
      parentRoute,
      space,
    }),
    [documentsFromCollection, parentRoute, space]
  )

  if (filtersLoading) {
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Modals />
      <ItemActionModals />
      <Box flexDirection="column" height="full">
        <Box flexGrow paddingX="s12" paddingTop="s12">
          <OneDataCollection
            fullHeight
            emptyStates={{
              'no-data': {
                title: i18n.t('documents.documents_datatable.empty_state.blank_state.title'),
                description: '',
              },
              'no-results': {
                title: i18n.t('documents.documents_datatable.empty_state.with_filters.title'),
                description: i18n.t(
                  'documents.documents_datatable.empty_state.with_filters.description'
                ),
              },
            }}
            source={source}
            onBulkAction={handleBulkAction}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
      </Box>
      <Outlet context={contextValue} />
    </>
  )
}

export default TrashBinDataCollection
